import type { ReactElement, ReactNode } from "react";
import React from "react";

import type {
  BoxProps,
  TypographyColors,
  TypographyProps,
} from "@braintrust/braintrust-ui-components";
import {
  Box,
  IconButton,
  RoundedBox,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { LongArrowLeftIcon } from "@braintrust/braintrust-ui-components/Icons";
import { RouterLink } from "@js/components/link";

import style from "./style.module.scss";

export type DefaultActionBarProps = Omit<BoxProps, "content"> & {
  title: string | undefined;
  subtitle: ReactNode;
  actions?: ReactElement[];
  backUrl?: string;
  textColor?: TypographyColors;
  titleProps?: Optional<TypographyProps, "component">;
  subtitleProps?: Optional<TypographyProps, "component">;
  content?: ReactNode;
};

export const DefaultActionBar = ({
  title,
  subtitle,
  actions = [],
  backUrl,
  content,
  textColor = "white",
  titleProps,
  subtitleProps,
  sx,
  ...props
}: DefaultActionBarProps) => {
  return (
    <RoundedBox
      className={`${style.actionBar} capitalize-first-letter`}
      {...props}
      sx={{
        borderRadius: "24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 4,
        px: { xs: 3, sm: "36px" },
        py: { xs: 3, sm: "42px" },
        ...sx,
      }}
    >
      <Box>
        {typeof title !== "undefined" && (
          <Typography
            color={textColor}
            component="h2"
            variant="paragraph"
            size="large"
            {...titleProps}
          >
            {title}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {backUrl && (
            <IconButton
              href={backUrl}
              variant="transparent"
              RouterLink={RouterLink}
              aria-label="Go back"
            >
              <LongArrowLeftIcon sx={{ color: "var(--white)" }} />
            </IconButton>
          )}
          <Typography
            color={textColor}
            variant="label"
            size="small"
            component="p"
            fontWeight={400}
            {...subtitleProps}
          >
            {subtitle}
          </Typography>
        </Box>
        {content && <Box>{content}</Box>}
      </Box>
      <Box
        className={style.actionBarButtonsWrapper}
        sx={{ display: "flex", flexShrink: 0, alignItems: "center" }}
      >
        {actions.map((action, index) => {
          return (
            <Box key={action.key} sx={{ ml: index === 0 ? 0 : 1 }}>
              {action}
            </Box>
          );
        })}
      </Box>
    </RoundedBox>
  );
};
